import type {
  DailyNote,
  WeeklyNote,
  WeeklyNotePayload,
  WeeklyNotesData,
  WeeklyNotesParams,
} from '@/libs/interfaces'

import { mockDailyNotes } from '../fixtures/dailyNotes'
import { mockWeeklyNotes } from '../fixtures/weeklyNotes'
import { delay, genID, nowISO } from '../helpers'
import { createEntityStore } from '../store'

const weeklyNoteStore = createEntityStore<WeeklyNote, 'weekNoteID'>(mockWeeklyNotes, 'weekNoteID')
const dailyNoteStore = createEntityStore<DailyNote, 'storeDailyNoteID'>(
  mockDailyNotes,
  'storeDailyNoteID',
)

const findWeeklyNote = (storeID?: string, week?: string): WeeklyNote | undefined =>
  weeklyNoteStore.find((note) => note.storeID === storeID && note.week === week).at(0)

const findDailyNotesForWeek = (storeID?: string): DailyNote[] =>
  dailyNoteStore.find((note) => note.storeID === storeID)

export const weeklyNotesApiMock = {
  getWeeklyNotes: async (params: WeeklyNotesParams): Promise<WeeklyNotesData> => {
    const weekNote = findWeeklyNote(params.storeID, params.week)
    return delay({
      weekNote:
        weekNote ??
        ({
          weekNoteID: '',
          storeID: params.storeID ?? '',
          week: params.week ?? '',
          note: '',
          createdAt: nowISO(),
          updatedAt: nowISO(),
        } satisfies WeeklyNote),
      dailyNotes: findDailyNotesForWeek(params.storeID),
    })
  },

  createOrUpdateWeeklyNotes: async (payload: WeeklyNotePayload): Promise<WeeklyNotesData> => {
    const existing = findWeeklyNote(payload.storeID, payload.week)
    const weekNote: WeeklyNote = existing
      ? (weeklyNoteStore.update(existing.weekNoteID, {
          note: payload.weekNote,
          updatedAt: nowISO(),
        }) ?? existing)
      : weeklyNoteStore.insert({
          weekNoteID: genID('weekly-note'),
          storeID: payload.storeID,
          week: payload.week,
          note: payload.weekNote,
          createdAt: nowISO(),
          updatedAt: nowISO(),
        })

    const savedDailyNotes = payload.dailyNotes.map((input) => {
      const existingDaily = dailyNoteStore
        .find((note) => note.storeID === payload.storeID && note.date === input.date)
        .at(0)

      if (existingDaily) {
        return (
          dailyNoteStore.update(existingDaily.storeDailyNoteID, {
            note: input.note,
            updatedAt: nowISO(),
          }) ?? existingDaily
        )
      }

      return dailyNoteStore.insert({
        storeDailyNoteID: genID('daily-note'),
        storeID: payload.storeID,
        date: input.date,
        note: input.note,
        createdAt: nowISO(),
        updatedAt: nowISO(),
      })
    })

    return delay({ weekNote, dailyNotes: savedDailyNotes })
  },
}
